import { z } from 'zod';

import { apenasDigitos, telefoneBrValido } from '../shared/documento';
import { ContaAsaasDoParceiro, InformarWalletInput } from './asaas-parceiro';
import { instante } from './common';
import { defineEndpoint } from './endpoint';
import { ComRequestId, defineJobContract } from './jobs';

// Onboarding do parceiro (docs/plans/2026-10-27-carteira-vendedor/ONBOARDING.md).

/** Onde o parceiro novo está; nula no banco = fora do onboarding (os parceiros antigos). */
export const ETAPAS_ONBOARDING = ['aguardando_assinatura', 'aguardando_acesso', 'boas_vindas'] as const;
export const EtapaOnboarding = z.enum(ETAPAS_ONBOARDING);
export type EtapaOnboarding = z.infer<typeof EtapaOnboarding>;

/** O que a Aliança vê: a etapa e, quando parada por prazo, o porquê. */
export const SITUACOES_ONBOARDING = ['aguardando_assinatura', 'assinatura_expirada', 'aguardando_acesso', 'convite_vencido', 'boas_vindas'] as const;
export const SituacaoOnboarding = z.enum(SITUACOES_ONBOARDING);
export type SituacaoOnboarding = z.infer<typeof SituacaoOnboarding>;

/** Parado por prazo vencido: só anda quando a Aliança age (envia o contrato ou o convite de novo). */
export const SITUACOES_TRAVADAS: readonly SituacaoOnboarding[] = ['assinatura_expirada', 'convite_vencido'];

/** O que o token do convite mostra antes do login (`POST /api/auth/boas-vindas/convite`): os dados do contrato. */
export const ConviteDasBoasVindas = z.object({ email: z.string(), nome: z.string(), cpf: z.string(), telefone: z.string(), empresa: z.string() });
export type ConviteDasBoasVindas = z.infer<typeof ConviteDasBoasVindas>;

/** As etapas das boas-vindas, na ordem; do acesso em diante, o servidor guarda em qual o representante está. */
export const PASSOS_DAS_BOAS_VINDAS = { acesso: 1, empresa: 2, recebimento: 3, equipe: 4 } as const;
export const PRIMEIRO_PASSO_DAS_BOAS_VINDAS = PASSOS_DAS_BOAS_VINDAS.empresa;
export const ULTIMO_PASSO_DAS_BOAS_VINDAS = PASSOS_DAS_BOAS_VINDAS.equipe;

/** Boas-vindas sem avanço há mais que isto entra na fila "Boas-vindas parada". */
export const BOAS_VINDAS_PARADA_DIAS = 3;

/** Convite ao representante depois da assinatura: enfileirado na mesma transação, emitido pelo worker. */
export const jobParceiroConvidarRepresentante = defineJobContract({
  name: 'parceiro.convidar-representante',
  schema: ComRequestId.extend({ parceiroId: z.uuid() }),
});

/** Onde o parceiro recebe a comissão enquanto não há repasse automático (ONBOARDING D14). */
export const Recebimento = z.discriminatedUnion('tipo', [
  z.strictObject({ tipo: z.literal('pix'), chave: z.string().trim().min(1, 'Informe a chave Pix').max(140) }),
  z.strictObject({
    tipo: z.literal('banco'),
    banco: z.string().trim().min(1, 'Informe o banco').max(120),
    agencia: z.string().trim().min(1, 'Informe a agência').max(20),
    conta: z.string().trim().min(1, 'Informe a conta').max(30),
  }),
]);
export type Recebimento = z.infer<typeof Recebimento>;

/** O que o parceiro edita da própria empresa (lista branca): nas boas-vindas e em "Minha empresa". */
export const EmpresaEditavel = z.strictObject({
  nome_fantasia: z.string().trim().max(120).nullable(),
  telefone: z.string().transform(apenasDigitos).refine(telefoneBrValido, { message: 'Confira o telefone: com DDD, 10 ou 11 números.' }),
});
export type EmpresaEditavel = z.infer<typeof EmpresaEditavel>;

/** A empresa como o parceiro a vê: o que está no contrato (só leitura) e o que ele edita. */
export const EmpresaDoParceiro = z.object({
  razao_social: z.string(),
  cnpj: z.string(),
  endereco: z.string().nullable(),
  nome_fantasia: z.string().nullable(),
  telefone: z.string(),
  logo_url: z.string().nullable(),
});
export type EmpresaDoParceiro = z.infer<typeof EmpresaDoParceiro>;

export const BoasVindas = z.object({
  passo: z.number().int().min(PRIMEIRO_PASSO_DAS_BOAS_VINDAS).max(ULTIMO_PASSO_DAS_BOAS_VINDAS),
  representante: z.object({ nome: z.string().nullable(), cpf: z.string().nullable(), celular: z.string().nullable() }),
  empresa: EmpresaDoParceiro,
  recebimento: Recebimento.nullable(),
  recebimento_obrigatorio: z.boolean(),
});
export type BoasVindas = z.infer<typeof BoasVindas>;

export const MAX_CONVITES_DAS_BOAS_VINDAS = 20;

export const ConcluirBoasVindas = z.strictObject({
  convites: z.array(z.string().trim().toLowerCase().pipe(z.email('E-mail inválido'))).max(MAX_CONVITES_DAS_BOAS_VINDAS),
  papel: z.enum(['usuario', 'gestor']),
});
export type ConcluirBoasVindas = z.infer<typeof ConcluirBoasVindas>;
/** Os papéis que o representante convida nas boas-vindas. */
export const PAPEIS_DO_CONVITE_DAS_BOAS_VINDAS = ConcluirBoasVindas.shape.papel.options;

const OK = z.object({ ok: z.literal(true) });

export const lerBoasVindas = defineEndpoint({ method: 'GET', path: '/api/boas-vindas', output: BoasVindas });
export const salvarEmpresaDasBoasVindas = defineEndpoint({ method: 'PUT', path: '/api/boas-vindas/empresa', body: EmpresaEditavel, output: OK });
export const salvarRecebimentoDasBoasVindas = defineEndpoint({ method: 'PUT', path: '/api/boas-vindas/recebimento', body: Recebimento, output: OK });
/** Recebimento pelo Asaas (a conta grava pelos endpoints dela) ou deixado para depois: só avança. */
export const avancarBoasVindas = defineEndpoint({ method: 'POST', path: '/api/boas-vindas/avancar', output: OK });
/** "Já tenho conta Asaas" nas boas-vindas: grava o wallet e avança numa gravação só. */
export const informarWalletDasBoasVindas = defineEndpoint({ method: 'PUT', path: '/api/boas-vindas/wallet', body: InformarWalletInput, output: ContaAsaasDoParceiro });
export const concluirBoasVindas = defineEndpoint({ method: 'POST', path: '/api/boas-vindas/concluir', body: ConcluirBoasVindas, output: OK });

/** "Minha empresa" do parceiro liberado (ONBOARDING D20): a empresa, o recebimento e quem o trocou por último. */
export const MinhaEmpresa = z.object({
  empresa: EmpresaDoParceiro,
  recebimento: Recebimento.nullable(),
  troca_do_recebimento: z.object({ por: z.string().nullable(), em: instante }).nullable(),
  /** Salvar, trocar o logo e o recebimento (`parceiros.minha-empresa.salvar`): falso impersonando. */
  pode_editar: z.boolean(),
});
export type MinhaEmpresa = z.infer<typeof MinhaEmpresa>;

export const lerMinhaEmpresa = defineEndpoint({ method: 'GET', path: '/api/minha-empresa', output: MinhaEmpresa });
export const salvarMinhaEmpresa = defineEndpoint({ method: 'PUT', path: '/api/minha-empresa', body: EmpresaEditavel, output: OK });
/** Trocar onde recebe pede a senha de quem troca (C12). */
export const trocarRecebimento = defineEndpoint({
  method: 'PUT',
  path: '/api/minha-empresa/recebimento',
  body: z.strictObject({ recebimento: Recebimento, senha: z.string().min(1, 'Digite sua senha') }),
  output: OK,
});
/** Multipart `file` (perfil do logo): nas boas-vindas e em "Minha empresa". */
export const enviarLogoDaMinhaEmpresa = defineEndpoint({ method: 'PUT', path: '/api/minha-empresa/logo', output: z.object({ logo_url: z.string().nullable() }) });

/** O onboarding de um parceiro numa linha da lista: a situação, há quantos dias e, nas boas-vindas, a etapa. */
export const OnboardingNaLista = z.object({ situacao: SituacaoOnboarding, dias: z.number().int(), passo: z.number().int().nullable() });
export type OnboardingNaLista = z.infer<typeof OnboardingNaLista>;

/** A faixa da ficha: a situação (nula = fora do onboarding), os marcos e o que a pessoa pode fazer. */
export const OnboardingDoParceiro = z.object({
  situacao: SituacaoOnboarding.nullable(),
  dias: z.number().int().nullable(),
  passo: z.number().int().nullable(),
  marcos: z.object({ cadastrado: instante.nullable(), assinado: instante.nullable(), primeiro_acesso: instante.nullable(), liberado: instante.nullable() }),
  pode_reenviar_convite: z.boolean(),
});
export type OnboardingDoParceiro = z.infer<typeof OnboardingDoParceiro>;

export const lerOnboardingDoParceiro = defineEndpoint({
  method: 'GET',
  path: '/api/parceiros/:id/onboarding',
  params: z.object({ id: z.uuid() }),
  output: OnboardingDoParceiro,
});
